<div class="queue-admin" [class.drawer-open]="!!selectedJob()">
  <header class="queue-admin__header">
    <div>
      <h2>Queue Admin</h2>
      <p>
        @if (overview(); as ov) {
          <span class="env-meta">
            Env <strong>{{ ov.env }}</strong>
            <span aria-hidden="true">/</span>
            {{ ov.totalQueues }} queues
          </span>
        } @else {
          Observe and control BullMQ jobs across all queues
        }
      </p>
    </div>
    <div class="queue-admin__header-actions">
      <span class="poll-hint">Auto-refresh every 4s</span>
      <button mat-stroked-button type="button" (click)="refreshAll()" [disabled]="overviewLoading() || jobsLoading()">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </header>

  <section class="queue-admin__filters" aria-label="Job filters">
    <div class="queue-admin__filters-grid">
      <div class="form-field-container">
        <label for="queue-filter-group" class="form-field-label">Group</label>
        <mat-form-field class="gingr-input-wrapper compact-field w-full" appearance="outline" subscriptSizing="dynamic">
          <mat-select
            id="queue-filter-group"
            [ngModel]="filterGroup()"
            (ngModelChange)="onGroupFilterChange($event)"
            placeholder="All groups"
          >
            <mat-option value="">All groups</mat-option>
            @for (group of groupOptions(); track group) {
              <mat-option [value]="group">{{ group }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-field-container">
        <label for="queue-filter-queue" class="form-field-label">Queue</label>
        <mat-form-field class="gingr-input-wrapper compact-field w-full" appearance="outline" subscriptSizing="dynamic">
          <mat-select
            id="queue-filter-queue"
            [ngModel]="filterQueue()"
            (ngModelChange)="filterQueue.set($event)"
            placeholder="All queues"
          >
            <mat-option value="">All queues</mat-option>
            @for (queue of queueOptions(); track queue) {
              <mat-option [value]="queue">{{ queue }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-field-container">
        <label for="queue-filter-status" class="form-field-label">Status</label>
        <mat-form-field class="gingr-input-wrapper compact-field w-full" appearance="outline" subscriptSizing="dynamic">
          <mat-select
            id="queue-filter-status"
            [ngModel]="filterStatus()"
            (ngModelChange)="filterStatus.set($event)"
            placeholder="All statuses"
          >
            <mat-option value="">All statuses</mat-option>
            @for (status of statuses; track status) {
              <mat-option [value]="status">{{ status }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>

      <div class="form-field-container form-field-container--grow">
        <label for="queue-filter-name" class="form-field-label">Job name</label>
        <mat-form-field class="gingr-input-wrapper compact-field w-full" appearance="outline" subscriptSizing="dynamic">
          <input
            id="queue-filter-name"
            matInput
            type="text"
            placeholder="e.g. processDossierFile"
            [ngModel]="filterName()"
            (ngModelChange)="filterName.set($event)"
            (keyup.enter)="applyFilters()"
          />
        </mat-form-field>
      </div>

      <div class="queue-admin__filter-actions">
        <button mat-raised-button color="accent" type="button" class="filter-action-btn" (click)="applyFilters()">
          Apply
        </button>
        <button mat-stroked-button type="button" class="filter-action-btn" (click)="clearFilters()">
          Clear
        </button>
      </div>
    </div>
  </section>

  <section class="queue-admin__overview" aria-label="Queue overview">
    <div class="section-heading">
      <h3>Queues</h3>
      @if (overview(); as ov) {
        <span>{{ ov.totalQueues }} total</span>
      }
    </div>

    @if (overviewLoading() && !overview()) {
      <div class="loading-row">
        <mat-spinner diameter="28"></mat-spinner>
        <span>Loading queues…</span>
      </div>
    } @else if (queueRows().length === 0) {
      <p class="empty-hint">No queues discovered.</p>
    } @else {
      <div class="table-panel">
        <table mat-table [dataSource]="queueRows()" class="queues-table">
          <ng-container matColumnDef="group">
            <th mat-header-cell *matHeaderCellDef>Group</th>
            <td mat-cell *matCellDef="let row">{{ row.group }}</td>
          </ng-container>

          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef>Queue</th>
            <td mat-cell *matCellDef="let row" class="mono" [matTooltip]="row.name">
              {{ row.name }}
              @if (row.error) {
                <span class="queue-error" [matTooltip]="row.error">error</span>
              }
            </td>
          </ng-container>

          <ng-container matColumnDef="active">
            <th mat-header-cell *matHeaderCellDef>Active</th>
            <td mat-cell *matCellDef="let row">
              <span class="metric" [class]="metricClass('active', countOf(row, 'active'))">
                {{ countOf(row, 'active') }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="waiting">
            <th mat-header-cell *matHeaderCellDef>Waiting</th>
            <td mat-cell *matCellDef="let row">
              <span class="metric" [class]="metricClass('waiting', countOf(row, 'waiting'))">
                {{ countOf(row, 'waiting') }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="delayed">
            <th mat-header-cell *matHeaderCellDef>Delayed</th>
            <td mat-cell *matCellDef="let row">
              <span class="metric" [class]="metricClass('delayed', countOf(row, 'delayed'))">
                {{ countOf(row, 'delayed') }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="failed">
            <th mat-header-cell *matHeaderCellDef>Failed</th>
            <td mat-cell *matCellDef="let row">
              <span class="metric" [class]="metricClass('failed', countOf(row, 'failed'))">
                {{ countOf(row, 'failed') }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="completed">
            <th mat-header-cell *matHeaderCellDef>Completed</th>
            <td mat-cell *matCellDef="let row">
              <span class="metric" [class]="metricClass('completed', countOf(row, 'completed'))">
                {{ countOf(row, 'completed') }}
              </span>
            </td>
          </ng-container>

          <ng-container matColumnDef="paused">
            <th mat-header-cell *matHeaderCellDef>Paused</th>
            <td mat-cell *matCellDef="let row">
              <span class="metric" [class]="metricClass('paused', countOf(row, 'paused'))">
                {{ countOf(row, 'paused') }}
              </span>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="queueColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: queueColumns"
            class="queues-table__row"
            [class.queues-table__row--active]="filterQueue() === row.name"
            (click)="selectQueue(row)"
          ></tr>
        </table>
      </div>
    }
  </section>

  @if (selectedCount() > 0) {
    <div class="bulk-toolbar" role="toolbar" aria-label="Bulk job actions">
      <span>{{ selectedCount() }} selected</span>
      <button mat-stroked-button type="button" [disabled]="actionBusy()" (click)="runBulkAction('retry')">Retry</button>
      <button mat-stroked-button type="button" [disabled]="actionBusy()" (click)="runBulkAction('promote')">Promote</button>
      <button mat-stroked-button type="button" [disabled]="actionBusy()" (click)="runBulkAction('cancel')">Cancel</button>
      <button mat-stroked-button type="button" [disabled]="actionBusy()" (click)="runBulkAction('remove')">Remove</button>
      <button mat-stroked-button color="warn" type="button" [disabled]="actionBusy()" (click)="runBulkAction('force-remove')">
        Force remove
      </button>
      <button mat-button type="button" (click)="clearSelection()">Clear selection</button>
    </div>
  }

  <section aria-label="Jobs">
    <div class="section-heading">
      <h3>Jobs</h3>
      <span>{{ total() }} matching</span>
    </div>

    <div class="queue-admin__table-wrap">
    @if (jobsLoading()) {
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    }

    <table
      mat-table
      [dataSource]="jobs()"
      class="jobs-table"
      matSort
      [matSortActive]="sortBy()"
      [matSortDirection]="sortDir()"
      matSortDisableClear
      (matSortChange)="handleSort($event)"
    >
      <ng-container matColumnDef="select">
        <th mat-header-cell *matHeaderCellDef>
          <mat-checkbox
            [checked]="isAllSelected()"
            [disabled]="jobs().length === 0"
            (change)="toggleSelectAll()"
            aria-label="Select all jobs on page"
          ></mat-checkbox>
        </th>
        <td mat-cell *matCellDef="let row">
          <mat-checkbox
            [checked]="isSelected(row)"
            (click)="$event.stopPropagation()"
            (change)="toggleSelect(row)"
            [attr.aria-label]="'Select job ' + row.id"
          ></mat-checkbox>
        </td>
      </ng-container>

      <ng-container matColumnDef="id">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>ID</th>
        <td mat-cell *matCellDef="let row">{{ row.id }}</td>
      </ng-container>

      <ng-container matColumnDef="name">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
        <td mat-cell *matCellDef="let row">{{ row.name }}</td>
      </ng-container>

      <ng-container matColumnDef="queue">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Queue</th>
        <td mat-cell *matCellDef="let row" class="mono truncate" [matTooltip]="row.queue">{{ row.queue }}</td>
      </ng-container>

      <ng-container matColumnDef="group">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Group</th>
        <td mat-cell *matCellDef="let row">{{ row.group }}</td>
      </ng-container>

      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
        <td mat-cell *matCellDef="let row">
          <span class="status-pill" [attr.data-status]="row.status">{{ row.status }}</span>
        </td>
      </ng-container>

      <ng-container matColumnDef="progress">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Progress</th>
        <td mat-cell *matCellDef="let row">{{ progressValue(row.progress) }}%</td>
      </ng-container>

      <ng-container matColumnDef="attempts">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Attempts</th>
        <td mat-cell *matCellDef="let row">{{ row.attemptsMade }}</td>
      </ng-container>

      <ng-container matColumnDef="createdAt">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Created</th>
        <td mat-cell *matCellDef="let row">
          {{ row.createdAt ? (row.createdAt | date: 'medium') : '-' }}
        </td>
      </ng-container>

      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef>Actions</th>
        <td mat-cell *matCellDef="let row" class="actions-cell">
          <div class="actions-cell__inner">
            @for (action of actionsFor(row.status); track action) {
              <button
                mat-stroked-button
                type="button"
                class="action-btn"
                [color]="action === 'force-remove' ? 'warn' : undefined"
                [disabled]="actionBusy()"
                (click)="runAction(row, action, $event)"
              >
                {{ actionLabel(action) }}
              </button>
            }
          </div>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="jobColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: jobColumns"
        class="jobs-table__row"
        [class.jobs-table__row--selected]="selectedJob()?.id === row.id && selectedJob()?.queue === row.queue"
        (click)="openDetail(row)"
      ></tr>
    </table>

    @if (!jobsLoading() && jobs().length === 0) {
      <p class="empty-hint">No jobs match the current filters.</p>
    }

    @if (total() > 0) {
      <app-list-paginator
        [length]="total()"
        [pageSize]="limit()"
        [pageIndex]="pageIndex()"
        [pageSizeOptions]="pageSizes"
        [includeAllOption]="true"
        [busy]="jobsLoading()"
        (page)="handlePage($event)"
      />
    }
    </div>
  </section>

  @if (selectedJob(); as job) {
    <aside class="detail-drawer" aria-label="Job detail">
      <div class="detail-drawer__header">
        <div>
          <h3>Job {{ job.id }}</h3>
          <p class="mono">{{ job.queue }}</p>
        </div>
        <button mat-icon-button type="button" (click)="closeDetail()" aria-label="Close job detail">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      @if (detailLoading()) {
        <div class="loading-row">
          <mat-spinner diameter="24"></mat-spinner>
          <span>Loading detail…</span>
        </div>
      } @else if (jobDetail(); as detail) {
        <div class="detail-drawer__meta">
          <div><span>Name</span><strong>{{ detail.name }}</strong></div>
          <div><span>Status</span><strong>{{ detail.status }}</strong></div>
          <div><span>Group</span><strong>{{ detail.group }}</strong></div>
          <div><span>Progress</span><strong>{{ progressValue(detail.progress) }}%</strong></div>
          <div><span>Attempts</span><strong>{{ detail.attemptsMade }}</strong></div>
          <div><span>Created</span><strong>{{ detail.createdAt ? (detail.createdAt | date: 'medium') : '-' }}</strong></div>
          <div><span>Processed</span><strong>{{ detail.processedOn ? (detail.processedOn | date: 'medium') : '-' }}</strong></div>
          <div><span>Finished</span><strong>{{ detail.finishedOn ? (detail.finishedOn | date: 'medium') : '-' }}</strong></div>
          @if (detail.failedReason) {
            <div class="full"><span>Failed reason</span><strong class="danger-text">{{ detail.failedReason }}</strong></div>
          }
          @if (detail.mongoFingerprintMismatch) {
            <div class="full warn-banner">Mongo fingerprint mismatch: job produced against a different cluster.</div>
          }
        </div>

        <div class="detail-drawer__actions">
          @for (action of actionsFor(detail.status); track action) {
            <button
              mat-stroked-button
              type="button"
              [color]="action === 'force-remove' ? 'warn' : undefined"
              [disabled]="actionBusy()"
              (click)="runAction(detail, action)"
            >
              {{ actionLabel(action) }}
            </button>
          }
        </div>

        <h4>Payload</h4>
        <pre class="code-block">{{ detail.data | json }}</pre>

        <h4>Logs ({{ jobLogs().length }})</h4>
        @if (jobLogs().length === 0) {
          <p class="empty-hint">No logs recorded for this job.</p>
        } @else {
          <pre class="code-block logs">@for (line of jobLogs(); track $index) {
{{ line }}
}</pre>
        }
      } @else {
        <p class="empty-hint">Could not load job detail.</p>
      }
    </aside>
  }
</div>
